import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../@types/redux';
import { dataSetPrintServers, dataSetSites } from '../../../store/masterData/Actions';
import { formatDateTime, isNullOrWhiteSpace, upsertArrayElement } from '../../../services/appFunctionsService';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../store/general/Functions';
import Screen from '../../../components/Screen';
import { ConfirmationDialog, CustomTable, ProjectDialog } from '@zz2/zz2-ui';
import PrintServerHttpService from '../../../services/http/masterData/printServerHttpService';
import SiteHttpService from '../../../services/http/masterData/siteHttpService';
import PrintServerForm from './form/PrintServerForm';
import { IApiError } from '../../../@types/other';
import { setPrintServerMasterDataIndexedDB, syncMasterData } from '../../../services/masterDataSyncService';
import { IPrintServer } from '../../../@types/model/masterData/printServer/printServer';
import UserHelper from '../../../services/helper/userHelper';
import { RightGrantsEnum } from '../../../@types/enum/rightGrantsEnum';
import PrintServerModelHelper from '../../../@types/model/masterData/printServer/printServerModelHelper';
import { useLocation } from 'react-router-dom';
import { IPrintServerFormValues } from '../../../@types/model/masterData/printServer/printServerFormValues';

const PrintServerScreen = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const printServers = useAppSelector(state => state.masterData.printServers);
    const sites = useAppSelector(state => state.masterData.sites);
    const location = useLocation();
    const currentUser = useAppSelector(x => x.auth.session?.user ?? null);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedPrintServer, setSelectedPrintServer] = useState<IPrintServer | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [deletingPrintServer, setDeletingPrintServer] = useState<IPrintServer | null>(null);

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        const fetchData = async () => {
            const isIndexedDBAvailable = !!self.indexedDB;
            if (isIndexedDBAvailable) {
                await syncMasterData(false);
            } else {
                setIsLoading(true);
                try {
                    const [printServerRes, siteRes] = await Promise.all([
                        PrintServerHttpService.getPrintServerData(),
                        SiteHttpService.getSiteData(),
                    ]);
                    dispatch(dataSetSites(siteRes.data));
                    dispatch(dataSetPrintServers(printServerRes.data));
                } catch (error : unknown) {
                    const apiError = error as IApiError;
                    generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading print servers.');
                } finally {
                    setIsLoading(false);
                }
            }
        };
        fetchData();
    }, [dispatch]);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    const onSubmit = async (values : IPrintServerFormValues) => {
        setIsLoading(true);

        try {
            const upsert = PrintServerModelHelper.createUpsert(values);
            const res = await PrintServerHttpService.addOrUpdatePrintServer(upsert);
            const updatedList = upsertArrayElement(printServers, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetPrintServers(updatedList));
            await setPrintServerMasterDataIndexedDB(updatedList);

            generalShowSuccessSnackbar(selectedPrintServer ? 'Print server updated successfully.' : 'Print server added successfully.');
            onModalDismiss();
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating print server data.');
        } finally {
            setIsLoading(false);
        }
    };

    const removePrintServer = async () => {
        if (!deletingPrintServer) return;
        const updatedPrintServer = { ...deletingPrintServer, isActive: false };
        setIsLoading(true);
        try {
            const res = await PrintServerHttpService.deletePrintServer(updatedPrintServer.id);
            const updatedList = upsertArrayElement(printServers, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetPrintServers(updatedList));
            await setPrintServerMasterDataIndexedDB(updatedList);
            generalShowSuccessSnackbar('Print server deleted successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting print server.');
        } finally {
            closeDeletePopup();
            setIsLoading(false);
        }
    };

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const openModal = () => {
        setIsDialogOpen(true);
    };

    const onModalDismiss = () => {
        setIsDialogOpen(false);
        setSelectedPrintServer(null);
    };

    const edit = (selectedRow : IPrintServer) => {
        setSelectedPrintServer(selectedRow);
        openModal();
    };

    const onDelete = (selectedRow : IPrintServer) : void => {
        setDeletingPrintServer(selectedRow);
    };

    const closeDeletePopup = () : void => {
        setDeletingPrintServer(null);
    };

    const deleteButtonTooltip = (row : IPrintServer) : string => {
        let message = 'Delete';

        if (!userHasRights.deleteRight) {
            message = UserHelper.getRelatedRightToolTipMessage('Print Server', RightGrantsEnum.Delete);
        } else if (!row.isActive) {
            message = 'Already Inactive.';
        }

        return message;
    };

    const getSiteShortDescription = useCallback((siteId : number) => {
        const site = sites?.find(x => x.id === siteId);
        return site?.shortDescription ?? '';
    }, [sites]);

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const initialValues = useMemo(() => {
        if (isDialogOpen) {
            return PrintServerModelHelper.createFormValues(selectedPrintServer, sites);
        }
    }, [selectedPrintServer, isDialogOpen, sites]);

    const userHasRights = useMemo(() => {
        return UserHelper.userRelatedRights(location.pathname, currentUser?.rights);
    }, [currentUser, location]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <Screen isLoading={isLoading}>
            <div className='fdc hfill'>
                <Card className='fdc hfill'>
                    <CustomTable<IPrintServer>
                    
                        enableAdding
                        // addTooltipRightValidation={!userHasRights.addRight}
                        disableAddButton={!userHasRights.addRight || isLoading}
                        addTooltip={!userHasRights.addRight ? UserHelper.getRelatedRightToolTipMessage('Print Server', RightGrantsEnum.Add) : 'Add'}
                        addFunction={openModal}
                        // addTooltipOnClick={() : void => requestAccess('Add')}
                    
                        enableEditing
                        // editTooltipRightValidation={!userHasRights.editRight}
                        disableEditButton={!userHasRights.editRight || isLoading}
                        editTooltip={!userHasRights.editRight ? UserHelper.getRelatedRightToolTipMessage('Print Server', RightGrantsEnum.Edit) : 'Edit'}
                        editFunction={edit}
                        // editTooltipOnClick={() : void => requestAccess('Edit')}

                        enableDeleting
                        // deleteTooltipRightValidation={!userHasRights.deleteRight}
                        disableDeleteButton={(row : IPrintServer) : boolean => !userHasRights.deleteRight || !row.isActive || isLoading}
                        deleteTooltip={deleteButtonTooltip}
                        deleteFunction={onDelete}
                        // deleteTooltipOnClick={() : void => requestAccess('Delete')}

                        enableSorting
                        enableFiltering
                        warning={row => !!row.primaryUrl && !row.isOverrideFlag}
                        fitWidthToPage
                        enablePagination
                        columns={[
                            { title: 'Name', field: 'name', enableFiltering: true, enableSorting: true },
                            { title: 'MAC Address', field: 'macAddress', enableFiltering: true, enableSorting: true },
                            { title: 'IP Address', field: 'ipAddress', enableFiltering: true, enableSorting: true },
                            { title: 'Primary URL', field: 'primaryUrl', enableFiltering: true, enableSorting: true },
                            { title: 'Default Scheme', field: 'defaultScheme', enableFiltering: true, enableSorting: true },
                            { title: 'Port', field: 'port', enableFiltering: true, enableSorting: true },
                            { title: 'Site', field: 'siteId', formatFunction: getSiteShortDescription, enableFiltering: true, enableSorting: true },
                            { title: 'Is Mafroda Integrated?', field: 'isMafrodaIntegrated', type: 'boolean', enableFiltering: true, enableSorting: true },
                            { title: 'Override Flag?', field: 'isOverrideFlag', type: 'boolean', enableFiltering: true, enableSorting: true },
                            { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                            { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                            { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                        ]}
                        rows={printServers}
                        initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                        pageSizes={[50, 150, 250, 500, 1000]}
                        pageHeight={190}
                        isActive={row => row.isActive}
                    />
                </Card>
            </div>
            {isDialogOpen && initialValues &&
                <ProjectDialog
                    title={selectedPrintServer ? 'Edit Print Server' : 'Add Print Server'}
                    isLoadingCircular={isLoading}
                    isOpen={isDialogOpen}
                    onClose={onModalDismiss}
                    maxWidth={'sm'}
                    fullWidth
                >
                    <PrintServerForm
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={onModalDismiss}
                        isLoading={isLoading}
                    />
                </ProjectDialog>
            }
            <ConfirmationDialog
                title={'Inactivate Print Server'}
                open={!!deletingPrintServer}
                description={'Are you sure you would like to set the following item to inactive?'}
                onAccept={removePrintServer}
                onClose={closeDeletePopup}
                dialogType={'red'}
                isLoading={isLoading}
            />
        </Screen>
    );
};

export default PrintServerScreen;
